import React, {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import moment, { Moment } from 'moment';
import { useTranslation } from 'react-i18next';
import {
    mixed, object, string, array,
} from 'yup';
import { Controller, useForm } from 'react-hook-form';
import {
    DatePicker, Modal, notification, NumberInput, Select, TextInput,
    CheckboxGroup, Infobox,
} from 'ui-library';
import { useYupValidationResolver } from 'hooks/useYupValidationResolver';
import { FILE_TYPES } from 'pages/ClientOnboardingFlow/pages/NewsPlan/UploadDocumentsPage/validation/const';
import {
    compareDates, dateFormatZurich, dateTimeStandardFormat, formatDateWithoutLocalConversion, isDateExpired,
} from 'utils/datetime';
import UploadDocuments from 'components/UploadDocuments/UploadDocuments';
import Error from 'components/AtomicStructure/atoms/Error';

import './UploadAdvisoryDocumentModal.css';
import { DEFAULT_APP_CURRENCY, NUMBER_AND_LETTERS_REGEX } from 'constants/constants';
import { getBase64FromFile } from 'utils/file';

import { getValidationMessage } from 'utils/validation';

import { ICON_INFO_OUTLINE } from 'ui-library/components/Icon';
import { getSetupData } from 'additiv-services/setup';
import { AdvisorySystem, getAdvisoryValidTillDate } from 'utils/advisoty';
import { DocumentPayload, DocumentType, useAdvisoryDocumentBase } from './hooks/useAdvisoryDocumentBase';

type UploadAdvisoryDocumentModalProps = {
    show: boolean;
    onClose: (documentItem: DocumentType | null) => void;
    contactGroupId: number;
    inAdvisoryFlow: boolean;
}

type AdvisoryDocumentForm = {
    date: Moment | Date | null;
    number: string;
    investmentAmount: number;
    system: string;
    file: any;
    upload_advisory_confirmation?: boolean[];
}

const getUploadAdvisoryCheckboxOptions = (t, disabled) => ([
    {
        label: t('advisoryDocuments.uploadDocument.uploadConfirmation.One'), value: '1', position: 1, disabled,
    },
    {
        label: t('advisoryDocuments.uploadDocument.uploadConfirmation.Two'), value: '2', position: 2, disabled,
    },
]);

export const UploadAdvisoryDocumentModal = ({
    show,
    onClose,
    contactGroupId,
}: UploadAdvisoryDocumentModalProps) => {
    const { t, i18n: { language } } = useTranslation();
    const {
        onContinue,
        saveInProgress,
    } = useAdvisoryDocumentBase();
    const [isSending, setSending] = useState<boolean>(false);
    const {
        mandatoryError,
    } = getValidationMessage();

    const schema = useMemo(() => object()
        .shape({
            date: string()
                .required(t('advisoryDocuments.uploadDocument.requiredField.advisoryDate'))
                .nullable(),
            number: string()
                .matches(NUMBER_AND_LETTERS_REGEX, t('advisoryDocuments.uploadDocument.numbersAndLettersOnly'))
                .required(t('advisoryDocuments.uploadDocument.requiredField.advisoryNumber')),
            system: string()
                .required(t('advisoryDocuments.uploadDocument.requiredField.advisorySystem')),
            investmentAmount: string()
                .required(t('advisoryDocuments.uploadDocument.requiredField.investmentAmount')),
            file: mixed()
                .required(t('advisoryDocuments.uploadDocument.requiredField.advisoryDocument'))
                .nullable(),
            upload_advisory_confirmation: array()
                .of(string()).required(mandatoryError),
        }), [language]);

    const resolver = useYupValidationResolver(schema);

    const {
        handleSubmit,
        formState: { errors },
        control,
        watch,
        reset,
        setValue,
        setError,
    } = useForm<AdvisoryDocumentForm>({
        resolver,
        defaultValues: {
            date: null,
            number: '',
            system: '',
            file: null,
            upload_advisory_confirmation: [],
        },
        shouldFocusError: false,
    });

    const date = watch('date');
    const selectedSystem = watch('system');
    const uploadedFile = watch('file');
    const upload_advisory_confirmation = watch('upload_advisory_confirmation');

    const advisorySystemOptions = useMemo(() => [
        ...(selectedSystem === '' ? [{
            value: '',
            label: <div className="empty-item" />,
        }] : []),
        ...Object.keys(AdvisorySystem)
            .filter(k => ![AdvisorySystem.ImpaQt, AdvisorySystem.DFS]?.includes(AdvisorySystem[k]))
            .map(key => ({
                value: AdvisorySystem[key],
                label: key,
            })),
    ], [t, selectedSystem]);

    const advisoryExpiryDate = useMemo(
        () => {
            const selectedSystemNumber = parseInt(selectedSystem, 10);

            return (
                (date && (typeof selectedSystem === 'number')) ? getAdvisoryValidTillDate({ advisoryDate: date, system: selectedSystemNumber }) : null);
        },
        [selectedSystem, date, getAdvisoryValidTillDate],
    );

    const hasAdvisoryExpired = isDateExpired(advisoryExpiryDate, dateFormatZurich);

    const onCancel = useCallback((documentItem: DocumentType | null) => {
        if (onClose) {
            onClose(documentItem);
        }
        reset();
    }, [reset, onClose]);

    const addDocument = handleSubmit(async values => {
        try {
            setSending(true);
            const fileBase64 = await getBase64FromFile(values.file || '');
            const splitFileString = fileBase64.split(',');

            const payload: DocumentPayload = {
                investmentAmount: +values.investmentAmount,
                contactGroupId,
                date: moment(values.date).format(dateTimeStandardFormat),
                number: values.number,
                system: parseInt(values.system, 10),
                document: {
                    filename: values.file?.name || '',
                    file: splitFileString?.[1],
                },
            };

            const docItem = await onContinue(payload);

            onCancel(docItem);
        } catch (err) {
            notification.open({
                content: t('general.somethingWentWrong'),
                type: 'error',
            });
        } finally {
            setSending(false);
        }
    });

    useEffect(() => {
        if (!uploadedFile) {
            setValue('upload_advisory_confirmation', []);
        }
    }, [uploadedFile]);

    return (
        <Modal
            className="upload-advisory-document-modal"
            visible={show}
            onCancel={() => onCancel(null)}
            title={t('advisoryDocuments.uploadDocument.title')}
            onOk={addDocument}
            okText={t('advisoryDocuments.uploadDocument.continueToProductSelection')}
            cancelText={t('clientDashboard.cancel')}
            confirmLoading={saveInProgress}
            width={780}
            okButtonProps={{ type: 'primary', disabled: saveInProgress || hasAdvisoryExpired || !upload_advisory_confirmation || (upload_advisory_confirmation?.length < 2) }}
            cancelButtonProps={{ disabled: saveInProgress }}
        >
            <div className="modal-body">
                <p>
                    {t('advisoryDocuments.uploadDocument.advisoryDate')}
                    {' '}
                    *
                </p>

                <Controller
                    name="date"
                    control={control}
                    render={({
                        field: {
                            onChange,
                            value,
                        },
                    }) => (
                        <DatePicker
                            disabled={!!isSending}
                            format={dateFormatZurich}
                            value={value && formatDateWithoutLocalConversion(value, dateFormatZurich)}
                            error={errors?.date?.message}
                            disabledDate={(current) => current && (!compareDates(new Date(), current))}
                            onChange={(momentDate) => onChange(momentDate)}
                            style={{ border: errors?.date ? '2px solid #fe7468' : '2px solid #C9D0D2' }}
                            required
                        />
                    )}
                />
                <p>
                    {t('advisoryDocuments.uploadDocument.advisoryNumber')}
                    {' '}
                    *
                </p>
                <Controller
                    name="number"
                    control={control}
                    render={({ field }) => (
                        <TextInput
                            disabled={!!isSending}
                            error={errors?.number?.message}
                            {...field}
                        />
                    )}
                />
                <p>
                    {t('advisoryDocuments.uploadDocument.advisorySystem')}
                    {' '}
                    *
                </p>
                <Controller
                    name="system"
                    control={control}
                    render={({ field }) => (
                        <Select
                            {...field}
                            disabled={!!isSending}
                            error={errors?.system?.message}
                            options={advisorySystemOptions}
                        />
                    )}
                />
                <p>
                    {t('advisoryDocuments.uploadDocument.investmentAmount')}
                    {' '}
                    *
                </p>
                <Controller
                    name="investmentAmount"
                    control={control}
                    render={({ field }) => (
                        <NumberInput
                            {...field}
                            data-cy="investmentAmount"
                            disabled={isSending}
                            name="investmentAmount"
                            error={errors?.investmentAmount?.message}
                            thousandSeparator="’"
                            decimalSeparator="."
                            prefix={`${DEFAULT_APP_CURRENCY} `}
                            allowNegativeValue={false}
                            textRight
                        />
                    )}
                />
                <Controller
                    name="file"
                    control={control}
                    render={({
                        field: {
                            onChange,
                            ref,
                        },
                    }) => ((
                        <div className="upload-file-container">
                            <div className="mb-8">{t('advisoryDocuments.uploadDocument.fileLabel')}</div>
                            <UploadDocuments
                                fileTypesAllowed={FILE_TYPES.pdf}
                                ref={ref}
                                onSuccess={(val) => {
                                    try {
                                        setError('file', { message: undefined });
                                        onChange(val[0]);
                                    } catch (err) {
                                        //
                                    }
                                }}
                                onError={(err) => {
                                    setError('file', { message: err });
                                }}
                                className={`uploadFile ${errors.file?.message ? 'error' : ''}`}
                                numberOfFiles={1}
                                maxMbFileSize={20}
                            />
                            {errors.file?.message && <Error>{(errors.file?.message).toString()}</Error>}
                        </div>
                    ))}
                />
                <div className="advisory-expiry">
                    <div>
                        <p>{t('advisoryDocuments.uploadDocument.validTo')}</p>
                        <span className={hasAdvisoryExpired ? 'expired' : ''}>
                            {advisoryExpiryDate}
                        </span>
                    </div>
                    {hasAdvisoryExpired
                        && <div className="expiry-note">{t('advisoryDocuments.uploadDocument.expiryNote')}</div>}
                </div>

                <div className="upload-advisory-checkboxes">
                    <p className={!uploadedFile ? 'checkbox-wrapper-disabled' : 'mb-10'}>{t('ApplicationDetails.DocBox.FollowUpInvestorProfileCheckBox.Label.One')}</p>
                    <Controller
                        name="upload_advisory_confirmation"
                        control={control}
                        render={({ field }) => (
                            <CheckboxGroup
                                {...field}
                                value={upload_advisory_confirmation ?? []}
                                options={getUploadAdvisoryCheckboxOptions(t, !uploadedFile) || []}
                                error={<div>{errors?.upload_advisory_confirmation?.message}</div>}
                                defaultValue={[]}
                                disabled={!uploadedFile}
                                isInQuestionnaire
                            />
                        )}
                    />
                </div>
            </div>

            <Infobox info icon={ICON_INFO_OUTLINE} className="mb-32 mt-30">
                {t('advisoryDocuments.uploadDocument.Info.ManualUploadRestrictions')}
            </Infobox>
        </Modal>
    );
};
